import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaTimes } from "react-icons/fa";
import { useCart } from 'context/CartContext';
import { useTranslation } from 'context/LanguageContext';
import { buildStorageUrl, shouldShowQuantity } from 'config/clientConfig';
import { getStockPolicy } from 'utils/stockPolicy';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [showModal, setShowModal] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const { t, language } = useTranslation();
  const isRTL = language === 'ar';

  if (!product) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const hasDiscount = (product.discount ?? 0) > 0;
  const displayPrice = product.selling_price ?? product.price ?? 0;
  const isOffer = product.is_offer;
  const {
    quantity: resolvedQuantity,
    isTracked,
    isLowStock,
    isOutOfStock,
    canAddToCartByStock,
  } = getStockPolicy(product);
  const resolvedProductId = product?.id ?? product?.product_id ?? product?.product?.id;
  const resolvedImagePath = (() => {
    if (!product?.image) return "";
    const rawImage = String(product.image);
    if (
      /^https?:\/\//i.test(rawImage) ||
      rawImage.startsWith("images/") ||
      rawImage.startsWith("storage/") ||
      rawImage.startsWith("products/")
    ) {
      return rawImage;
    }
    return `images/${rawImage}`;
  })();
  
  // Check if product has variants
  const hasSizes = product.sizes && product.sizes.length > 0;
  const hasColors = product.colors && product.colors.length > 0;
  const hasVariants = hasSizes || hasColors;

  const handleCardClick = () => {
    if (!resolvedProductId) {
      console.error("ProductCard: missing product id", product);
      return;
    }
    navigate(`/product/${resolvedProductId}`);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (!canAddToCartByStock) return;
    
    if (hasVariants) {
      setShowModal(true);
    } else {
      addToCart(product, 1);
    }
  };

  const handleConfirmAdd = () => {
    if (!canAddToCartByStock) return;

    if (hasSizes && !selectedSize) {
      alert('Please select a size');
      return;
    }
    if (hasColors && !selectedColor) {
      alert('Please select a color');
      return;
    }

    const productWithVariants = {
      ...product,
      selectedSize: selectedSize || null,
      selectedColor: selectedColor || null
    };

    addToCart(productWithVariants, 1);
    setShowModal(false);
    setSelectedSize('');
    setSelectedColor('');
  };

  return (
    <>
      <div 
        className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 w-full max-h-[400px]'
        onClick={handleCardClick}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Product Image */}
        <div className="relative w-full h-48 bg-gray-100 flex items-center justify-center">
          {product.image ? (
            <img 
                src={buildStorageUrl(resolvedImagePath)}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
          )}
          
          {/* Badges */}
          <div className={`absolute top-2 flex flex-col gap-1 ${isRTL ? 'right-2' : 'left-2'}`}>
            {isOffer && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                Offer
              </span>
            )}
            {hasDiscount && (
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                -{product.discount}%
              </span>
            )}
            {isLowStock && (
              <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                Low Stock
              </span>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="p-2">
          {/* Product Name */}
          <h3 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">
            {product.name}
          </h3>

          {/* Stock Info */}
          {isTracked && (
            <div className="text-xs text-gray-500 mb-1">
              {!isOutOfStock ? (
                <span className={isLowStock ? 'text-orange-600' : 'text-green-600'}>
                  {shouldShowQuantity()
                    ? `${resolvedQuantity} ${t("inStock")}`
                    : t("available")}
                </span>
              ) : (
                <span className="text-red-600">{t("outOfStock")}</span>
              )}
            </div>
          )}

          {/* Price with Cart Icon */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg text-gray-900">
                {formatPrice(displayPrice)}
              </span>
            </div>
            
            {/* Add to Cart Icon */}
            <button 
              onClick={handleAddToCart}
              className={`p-2 rounded-full transition-colors ${
                canAddToCartByStock
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!canAddToCartByStock}
              aria-label="Add to cart"
            >
              <FaShoppingCart className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Variant Selection Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              className={`absolute top-4 text-gray-500 hover:text-gray-700 ${isRTL ? 'left-4' : 'right-4'}`}
            >
              <FaTimes className="w-5 h-5" />
            </button>

            {/* Product Info */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {product.name}
              </h3>
              <p className="text-lg font-bold text-blue-600">
                {formatPrice(displayPrice)}
              </p>
            </div>

            {/* Size Selection */}
            {hasSizes && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                 {t("selectSize")} <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 border rounded-lg transition-colors ${
                        selectedSize === size
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {hasColors && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t("selectColor")} <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border rounded-lg transition-colors ${
                        selectedColor === color
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleConfirmAdd}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {t("addtoCart")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
